// Node 4, the field demonstration, closes itself on two demo JSPs.
// Words: RECORDS_WORDS.md rev 2 (Robert, 2026-10-12). Replaces the three stations in
// JSP_WRITES_WORDS.md section 4 and GATES_AT_CUTOVER_WORDS.md.
// Pure: the server step and the Training Portal share it, and it is unit tested.
//
// Rules (Robert, 2026-10-12):
// * Two JSPs on any demo asset. One represents troubleshooting: a Tier 2 JSP.
//   One represents the ESWC: a Tier 3 JSP on the de-energized path.
// * A JSP counts when he submits it. The first of each kind is kept.
// * The second one writes his Node 4 record with both JSPs as evidence.
// * Once Node 4 is done, demo JSPs change nothing.
// * Close by Hand stays as the backup.

import type { DemoKey } from "./guest-asset";

export type StationKey = "troubleshooting" | "eswc";

export const STATIONS: readonly { key: StationKey; label: string }[] = [
  { key: "troubleshooting", label: "Troubleshooting, Tier 2" },
  { key: "eswc", label: "ESWC, Tier 3" },
];

/** One station done: the JSP that did it. The detail fields ride into the Node 4 evidence. */
export interface StationDone {
  jspNumber: string;
  doneOn: string;
  /** ISO time of Submit. Blank on progress saved before 2026-10-12. */
  doneAt?: string;
  assetId?: string;
  taskName?: string;
  tier?: number;
}

/** Stored on people/{qewId} as fieldDemoStations. Written by the server at Submit. */
export type StationProgress = Partial<Record<StationKey, StationDone>>;

/** The station a submitted demo JSP counts toward, or null. */
export function stationOf(demoKey: DemoKey | null, tier: number, isDeEnergized: boolean): StationKey | null {
  if (!demoKey) return null;
  if (tier === 2) return "troubleshooting";
  if (tier === 3 && isDeEnergized) return "eswc";
  return null;
}

/** Keys saved under the three-station rule, read as the two-JSP rule. */
const OLD_KEYS: Readonly<Record<string, StationKey>> = {
  msdTier2: "troubleshooting",
  msdTier3: "eswc",
  mccPanelTier3: "eswc",
};

function doneFrom(v: unknown): StationDone | null {
  if (!v || typeof v !== "object") return null;
  const r = v as Record<string, unknown>;
  if (typeof r.jspNumber !== "string" || typeof r.doneOn !== "string") return null;
  const out: StationDone = { jspNumber: r.jspNumber, doneOn: r.doneOn };
  if (typeof r.doneAt === "string") out.doneAt = r.doneAt;
  if (typeof r.assetId === "string") out.assetId = r.assetId;
  if (typeof r.taskName === "string") out.taskName = r.taskName;
  if (typeof r.tier === "number") out.tier = r.tier;
  return out;
}

/** The progress cleaned from a person record. Anything unknown is dropped. */
export function progressFrom(raw: unknown): StationProgress {
  const out: StationProgress = {};
  if (!raw || typeof raw !== "object") return out;
  const r = raw as Record<string, unknown>;
  for (const { key } of STATIONS) {
    const d = doneFrom(r[key]);
    if (d) out[key] = d;
  }
  for (const [old, key] of Object.entries(OLD_KEYS)) {
    const d = out[key] ? null : doneFrom(r[old]);
    if (d) out[key] = d;
  }
  return out;
}

/** The progress after this JSP. A station already done keeps its first JSP. */
export function withStation(p: StationProgress, key: StationKey, done: StationDone): StationProgress {
  return p[key] ? p : { ...p, [key]: done };
}

export function stationsDone(p: StationProgress): number {
  return STATIONS.filter((s) => p[s.key]).length;
}

export function allStationsDone(p: StationProgress): boolean {
  return stationsDone(p) === STATIONS.length;
}

/** The Node 4 evidence line: both JSP numbers in station order. */
export function evidenceOf(p: StationProgress): string {
  return `Field demonstration · ${STATIONS.map((s) => p[s.key]?.jspNumber ?? "").filter(Boolean).join(", ")}`;
}

// ---- Node 4 demonstration evidence (RECORDS_WORDS.md rev 2, "Kept clean for reports") ----
// The same shape for a new trainee and for a QEW carried over from the Sheet, so one report reads both.

export type DemonstrationBasis = "JSP on record" | "Instructor attests, app timeouts" | "Instructor attests, before the demo JSP";

export const DEMONSTRATION_BASES: readonly DemonstrationBasis[] = [
  "JSP on record",
  "Instructor attests, app timeouts",
  "Instructor attests, before the demo JSP",
];

/** One demonstration JSP. Old Sheet JSPs may have no number; doneAt always names the row. */
export interface DemonstrationJsp {
  jspNumber: string;
  doneAt: string;
  assetId: string;
  taskName: string;
  tier: number | null;
  eswcComplete: boolean;
}

/** One other piece of evidence, by tab and row ID. */
export interface SupportingEvidence {
  source: string;
  rowId: string;
  detail: string;
}

export interface Demonstration {
  demonstrationBasis: DemonstrationBasis;
  troubleshootingJsp: DemonstrationJsp | null;
  eswcJsp: DemonstrationJsp | null;
  supportingEvidence: SupportingEvidence[];
  attestedBy: string;
  attestedOn: string;
  sheetCopy: string;
}

function jspOf(d: StationDone | undefined, isEswc: boolean): DemonstrationJsp | null {
  if (!d) return null;
  return {
    jspNumber: d.jspNumber,
    doneAt: d.doneAt ?? d.doneOn,
    assetId: d.assetId ?? "",
    taskName: d.taskName ?? "",
    tier: d.tier ?? null,
    eswcComplete: isEswc,
  };
}

/** The evidence a new trainee's Node 4 carries when both JSPs are in. */
export function demonstrationOf(p: StationProgress): Demonstration {
  return {
    demonstrationBasis: "JSP on record",
    troubleshootingJsp: jspOf(p.troubleshooting, false),
    eswcJsp: jspOf(p.eswc, true),
    supportingEvidence: [],
    attestedBy: "",
    attestedOn: "",
    sheetCopy: "",
  };
}
